import _mapValues from 'lodash/mapValues';
import _merge from 'lodash/merge';
import _pick from 'lodash/pick';
import _omit from 'lodash/omit';
import queryString from 'query-string';

import DeviceProps from '@magnus/react-native-device-props';
import EvTruck from '@magnus/react-native-evtruck';
import { type IAttribution } from '@magnus/react-native-evtruck/types/interfaces';
import UserProps from '@magnus/react-native-user-props';

import Web2App from '@wowmaking/web2app';

import { disabled } from '@web-solutions/module-analytics';

// Check if code is running in browser
const isBrowser = typeof window !== 'undefined';

export type AppLinks = {
  ADJUST_LINK?: string;
  ADJUST_TOKEN?: string;
  APPSFLYER_LINK?: string;
  WEB_APP_LINK?: string;
}

type URLBaseParamsType = {
  urlExtraPath?: string;
  query?: Record<string, string>
}

let IDFM: string | undefined;
let appLinks: AppLinks | undefined;
let rcTarget: LinkTarget | undefined;

let URLBaseParams: URLBaseParamsType = {
  urlExtraPath: '',
  query: {}
};


export enum LinkTarget {
  DEFAULT = 'default', // empty - by default - go to AF/Adjust link and then to store
  WEB = 'web', // target is web app
}


export const UTM_PARAMS = [
  'utm_source',
  'campaign_name',
  'campaign_id',
  'creative_topic',
  'adset_name',
  'adset_id',
  'ad_name',
  'ad_id',
  'ad_subnetwork',
  'ad_keyword',
  'ad_placement',
];

const CLID_PARAMS = [
  'ttclid',
  'fbclid',
  'gbraid',
  'wbraid',
  'gclid',
  'gad_source',
  'ScCid',
  'ai',
  'epik',
  'utm_content',
  'utm_medium',
  'utm_term',
  'utm_id',
];

let _attribution: IAttribution | null = null;

const AttributionModule = {
  async init(links: AppLinks, options?: { fetchPrevInstall?: boolean }): Promise<IAttribution | null> {
    appLinks = links;

    IDFM = await DeviceProps.getIDFM();

    // Skip SSR - only run in browser
    if (!isBrowser) {
      return null;
    }

    const params = queryString.parse(window.location.search);

    if (!disabled) {
      let install = null
      try {
        //@ts-ignore
        install = options?.fetchPrevInstall ? await EvTruck.fetchWebAttribution() : null;
      } catch (e) {
        console.error(e);
      }
      const attribution = getCurrentAttribution();

      if (install?.utm_source === 'Organic') {
        install.utm_source = '';
      }

      if (!install?.utm_source || attribution?.utm_source) {
        EvTruck.trackWebAttribution({
          ...attribution,
          http_referer: window.document.referrer || '',
          //@ts-ignore
          url: queryString.stringifyUrl({
            url: window.location.origin + window.location.pathname,
            query: _omit(params, [...UTM_PARAMS, ...CLID_PARAMS, 'idfm', 'email']),
          }),
        });
      }

      Web2App.init({
        onChange: (system, p) => {
          // оборачиваем в массив, в будущем сможем поддерживать несколько пикселей на сайте и тп
          EvTruck.trackWebMeasurements({
            external_id: Web2App.getAttribution().web_external_id,
            //@ts-ignore
            propellerads_clid: params?.propellerads_clid,
            [system]: [p],
          });
        },
      });

      if (appLinks.ADJUST_LINK && appLinks.ADJUST_TOKEN) {
        Web2App.trackAdjustImpression(appLinks.ADJUST_TOKEN);
      }

      return _attribution = attribution?.utm_source ? attribution : (install || attribution);
    }

    return null;
  },

  getAttribution(): IAttribution & { install_time?: string } {
    return _attribution || getCurrentAttribution();
  },

  setURLBaseParams(params: URLBaseParamsType) {
    URLBaseParams = _merge(URLBaseParams, params)
  },

  setRemoteConfigTarget(target: LinkTarget) {
    rcTarget = target;
  },

  getMailLinkTarget(url: string) {
    return appLinks?.WEB_APP_LINK && url.indexOf(appLinks?.WEB_APP_LINK) === 0 ? LinkTarget.WEB : undefined;
  },

  async getLinkToApp(linkTarget?: LinkTarget, data?: any, appPlatform?: string, webAppLink?: string) {
    const { token, email, appLinkParams } = data || {}
     // Skip SSR - only run in browser
    if (!isBrowser) {
      return '';
    }

    const source = 'sub';
    const params = queryString.parse(window.location.search);

    linkTarget = linkTarget || rcTarget;

    if (!appLinks) {
      throw Error('Cannot create app link');
    }

    const query = {
      ...appLinkParams,
      source,
      mode: params.mode,
      utm_source: params.utm_source,
      campaign_id: params.campaign_id,
      creative_topic: params.creative_topic,
    }
    
    const up = await UserProps.fetch() as { utm_source: string, campaign_id: string, target: string, };

    const createLink = (url: string, otherParams?: object) => {
      return Promise.resolve(
        queryString.stringifyUrl({
          url,
          query: {
            ...query, 
            idfm: IDFM,
            source,
            mode: params.mode,
            utm_source: up.utm_source,
            campaign_id: up.campaign_id,
            ...URLBaseParams.query,
            ...otherParams
          },
        })
      )
    }

    if (appPlatform === 'web' && webAppLink) {
      return createLink(webAppLink, { token, email })
    }

    const w = appLinks.WEB_APP_LINK
      ? queryString.stringifyUrl({
        url: `${appLinks.WEB_APP_LINK}${URLBaseParams.urlExtraPath}`,
        query: {
          idfm: IDFM,
          ...query,
          ...URLBaseParams.query
        },
      })
      : null;

    if (linkTarget === LinkTarget.WEB && w) {
      return w;
    } else if (appLinks.APPSFLYER_LINK) {
      return Web2App.createAppsFlyerLink(appLinks.APPSFLYER_LINK, {
        uuid: IDFM,
        ...query,
        is_retargeting: true,
        token,
        email
      });
    } else if (appLinks.ADJUST_LINK && appLinks.ADJUST_TOKEN) {
      return Web2App.createAdjustLink(appLinks.ADJUST_LINK, appLinks.ADJUST_TOKEN, {
        uuid: IDFM,
        ...query,
        ...URLBaseParams.query,
        email
      });

    } else {
      throw Error('Cannot create app link');
    }
  },
};

export default AttributionModule;

function getCurrentAttribution() {
  // Skip SSR - only run in browser
  if (!isBrowser) {
    return {} as IAttribution;
  }
  
  const params = queryString.parse(window.location.search);
  return {
    ..._mapValues(_pick(params, UTM_PARAMS), i => String(i || '')),
    url: window.location.href,
  } as unknown as IAttribution
}
